import React, { useState } from "react";
import Breadcrumb from "@/components/Breadcrumb";
import SEO from "@/components/SEO";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Mail, MessageSquare, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error(t("contact.error")); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO title="Contact — ImageKitHub" description="Get in touch with the ImageKitHub team." />
      <Breadcrumb items={[{ label: t("nav.contact") }]} />
      <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">{t("contact.title")}</h1>
      <p className="mt-2 text-slate-500 dark:text-slate-400">{t("contact.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <IconInput icon={User} label={t("contact.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
          <IconInput icon={Mail} label={t("contact.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{t("contact.message")}</label>
          <textarea rows={6} required data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1c20] p-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        </div>
        <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60 transition-colors">
          <MessageSquare size={14} /> {sending ? t("contact.sending") : t("contact.send")}
        </button>
      </form>
    </div>
  );
}

function IconInput({ icon: I, label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
      <div className="relative">
        <I size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type={type} required={required} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1c20] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
      </div>
    </div>
  );
}
